import { useState, type CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import { format, formatDistanceToNow } from 'date-fns'
import {
  CheckCircle2,
  Calendar,
  Mail,
  Phone,
  Clock,
  MapPin,
  User,
  UserRound,
  Trash2,
  Edit2,
  Save,
  X,
  AlertTriangle,
  Circle,
} from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useUpdateActivity, useDeleteActivity } from '@/hooks/use-api'
import { useToast } from '@/hooks/use-toast'
import { isPastDate, toDisplayDate } from '@/lib/utils'
import { getTaskTypeIcon } from '@/lib/task-type'
import { SEMANTIC, ACTIVITY_TYPE_COLORS } from '@/lib/theme'
import type { Activity, ActivityType } from '@/types'
import { explainError } from '@/lib/explain-error'

interface ActivityTimelineItemProps {
  activity: Activity
  onEditTask?: (taskId: string) => void
  /** Click the status circle of an open task to complete it (confirmation handled by parent). */
  onCompleteTask?: (activity: Activity) => void
}

export function ActivityTimelineItem({
  activity,
  onEditTask,
  onCompleteTask,
}: ActivityTimelineItemProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [editedSubject, setEditedSubject] = useState(activity.subject)
  const [editedDescription, setEditedDescription] = useState(activity.description || '')

  const description = activity.description || ''
  // Collapse to 2 lines (with a Show more/less toggle) once a description would exceed ~2 lines —
  // i.e. longish single paragraphs or anything with 3+ lines. Keeps timeline cards compact.
  const isLongDescription = description.length > 140 || (description.match(/\n/g)?.length ?? 0) >= 2
  const contactName = activity.related_to?.who_name
  const whoId = activity.related_to?.who_id
  const whoType = activity.related_to?.who_type
  const whoEmail = activity.related_to?.who_email
  // Soft-deleted contacts/leads render as "[Deleted ...]" — don't link those.
  const isDeletedWho = contactName?.startsWith('[Deleted')
  const whoHref =
    whoId && !isDeletedWho && whoType === 'Contact'
      ? `/contacts/${whoId}`
      : whoId && !isDeletedWho && whoType === 'Lead'
        ? `/leads/${whoId}`
        : null

  const updateMutation = useUpdateActivity()
  const deleteMutation = useDeleteActivity()
  const { toast } = useToast()

  // Determine if a task is overdue
  const isOverdue =
    activity.activity_type === 'task' &&
    activity.status !== 'Completed' &&
    !!activity.activity_date &&
    isPastDate(activity.activity_date)

  const getActivityIcon = (type: ActivityType) => {
    // Tasks use status-specific icons
    if (type === 'task') {
      if (activity.status === 'Completed')
        return <CheckCircle2 className="h-5 w-5" aria-hidden="true" />
      if (isOverdue) return <AlertTriangle className="h-5 w-5" aria-hidden="true" />
      if (activity.status === 'In Progress') return <Clock className="h-5 w-5" aria-hidden="true" />
      return <Circle className="h-5 w-5" aria-hidden="true" />
    }
    switch (type) {
      case 'event':
        return <Calendar className="h-5 w-5" aria-hidden="true" />
      case 'email':
        return <Mail className="h-5 w-5" aria-hidden="true" />
      case 'call':
        return <Phone className="h-5 w-5" aria-hidden="true" />
    }
  }

  // GRK-BRAND-3: icon-circle + card-stripe colours from the semantic / activity
  // palette. Tasks colour by status (SEMANTIC); other activities by type
  // (ACTIVITY_TYPE_COLORS). Returned as {bg, fg, bd} hexes.
  const activityPalette = (type: ActivityType) => {
    if (type === 'task') {
      const s =
        activity.status === 'Completed'
          ? SEMANTIC.success
          : isOverdue
            ? SEMANTIC.error
            : activity.status === 'In Progress'
              ? SEMANTIC.info
              : SEMANTIC.neutral
      return { bg: s.fill, fg: s.text, bd: s.text }
    }
    const c =
      type === 'event'
        ? ACTIVITY_TYPE_COLORS.Event
        : type === 'email'
          ? ACTIVITY_TYPE_COLORS.Email
          : ACTIVITY_TYPE_COLORS.Call
    return { bg: c.tint, fg: c.solid, bd: c.solid }
  }

  // Base colours as CSS vars so the hover:*-success token classes on the
  // clickable circle still cascade over them.
  const getActivityIconVars = (type: ActivityType): CSSProperties => {
    const p = activityPalette(type)
    return { '--ci-bg': p.bg, '--ci-fg': p.fg, '--ci-bd': p.bd } as CSSProperties
  }

  const getCardBorderStyle = (): CSSProperties => {
    if (activity.activity_type !== 'task') return {}
    return { borderLeftWidth: 4, borderLeftColor: activityPalette('task').bg }
  }

  const getStatusBadgeVariant = (
    type: ActivityType,
    status: string
  ): 'default' | 'secondary' | 'destructive' | 'success' | 'warning' | 'info' => {
    if (type === 'task') {
      if (status === 'Completed') return 'success'
      if (status === 'In Progress') return 'info'
      if (isOverdue) return 'destructive'
      return 'warning' // Not Started
    }
    if (type === 'email') {
      return status === 'Sent' ? 'default' : 'secondary'
    }
    return 'default'
  }

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({
        type: activity.activity_type,
        id: activity.id,
        updates: {
          subject: editedSubject,
          description: editedDescription,
        } as Partial<Activity>,
      })

      toast({
        title: 'Success',
        description: 'Activity updated successfully.',
      })

      setIsEditing(false)
    } catch (error) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to update activity'),
        variant: 'destructive',
      })
    }
  }

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this activity?')) {
      return
    }

    try {
      await deleteMutation.mutateAsync({
        type: activity.activity_type,
        id: activity.id,
      })

      toast({
        title: 'Success',
        description: 'Activity deleted successfully.',
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to delete activity'),
        variant: 'destructive',
      })
    }
  }

  const handleCancel = () => {
    setEditedSubject(activity.subject)
    setEditedDescription(activity.description || '')
    setIsEditing(false)
  }

  // toDisplayDate parses date-only / UTC-midnight values as the literal local
  // calendar date (no off-by-one); real timestamps pass through unchanged.
  const activityDateObj = toDisplayDate(activity.activity_date) ?? new Date(activity.activity_date)
  // Tasks carry a date-only DUE date — show the date itself (a task due today is
  // "Jun 2, 2026", not the misleading "13 hours ago" / "12:00 AM"). Events,
  // emails and calls are real timestamps, so they keep relative time + a time.
  const isDateOnlyActivity = activity.activity_type === 'task'
  const dateLabel = isDateOnlyActivity
    ? format(activityDateObj, 'MMM d, yyyy')
    : formatDistanceToNow(activityDateObj, { addSuffix: true })
  const dateTitle = isDateOnlyActivity
    ? format(activityDateObj, 'MMM d, yyyy')
    : format(activityDateObj, 'MMM d, yyyy h:mm a')

  // Status badge + activity/task-type label — shared by the edit and read views.
  // In the read view it sits inline with the subject to keep each card compact.
  const statusAndType = (
    <>
      <Badge variant={getStatusBadgeVariant(activity.activity_type, activity.status)}>
        {activity.status}
      </Badge>
      {activity.activity_type === 'task' ? (
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          {getTaskTypeIcon(activity.type, 'h-3.5 w-3.5')}
          <span className="sr-only">Task type: </span>
          {activity.type || 'Task'}
        </span>
      ) : (
        <span className="text-xs text-muted-foreground capitalize">
          <span className="sr-only">Activity type: </span>
          {activity.activity_type}
        </span>
      )}
    </>
  )

  return (
    <Card
      className="p-2.5 hover:shadow-md transition-shadow"
      style={getCardBorderStyle()}
      role="article"
      aria-label={`${activity.activity_type}: ${activity.subject}${isOverdue ? ' (overdue)' : ''}`}
    >
      <div className="flex items-start gap-2.5">
        {/* Activity Type Icon — for an open task, the circle is a button to complete it */}
        {activity.activity_type === 'task' && activity.status !== 'Completed' && onCompleteTask ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onCompleteTask(activity)
            }}
            className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center border-2 bg-[var(--ci-bg)] text-[var(--ci-fg)] border-[color:var(--ci-bd)] transition-colors cursor-pointer hover:border-success hover:bg-success/10 hover:text-success focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            style={getActivityIconVars(activity.activity_type)}
            aria-label={`Mark task "${activity.subject}" complete`}
            title="Mark task complete"
          >
            {getActivityIcon(activity.activity_type)}
          </button>
        ) : (
          <div
            className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center border-2 bg-[var(--ci-bg)] text-[var(--ci-fg)] border-[color:var(--ci-bd)]"
            style={getActivityIconVars(activity.activity_type)}
            aria-hidden="true"
          >
            {getActivityIcon(activity.activity_type)}
          </div>
        )}

        {/* Activity Content */}
        <div className="flex-1 min-w-0">
          {/* Header: Subject and Actions */}
          <div className="flex items-start justify-between gap-4 mb-1">
            <div className="flex-1 min-w-0">
              {isEditing ? (
                <>
                  <Input
                    value={editedSubject}
                    onChange={(e) => setEditedSubject(e.target.value)}
                    className="mb-2"
                    aria-label="Activity subject"
                  />
                  <div className="flex items-center gap-2 flex-wrap">{statusAndType}</div>
                </>
              ) : (
                <div className="flex items-center gap-x-2 gap-y-0.5 flex-wrap min-w-0">
                  <h3
                    className="text-sm font-semibold text-foreground truncate min-w-0"
                    title={activity.subject}
                  >
                    {activity.subject}
                  </h3>
                  {statusAndType}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-1 flex-shrink-0">
              {isEditing ? (
                <>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleSave}
                    disabled={updateMutation.isPending}
                    aria-label="Save changes"
                  >
                    <Save className="h-4 w-4" aria-hidden="true" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleCancel}
                    disabled={updateMutation.isPending}
                    aria-label="Cancel editing"
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      if (activity.activity_type === 'task' && onEditTask) {
                        onEditTask(activity.id)
                      } else {
                        setIsEditing(true)
                      }
                    }}
                    aria-label={
                      activity.activity_type === 'task' && onEditTask
                        ? 'Edit task details'
                        : 'Edit activity'
                    }
                  >
                    <Edit2 className="h-4 w-4" aria-hidden="true" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleDelete}
                    disabled={deleteMutation.isPending}
                    aria-label="Delete activity"
                  >
                    <Trash2 className="h-4 w-4 text-destructive" aria-hidden="true" />
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Description */}
          {(activity.description || isEditing) && (
            <div className="mb-1.5">
              {isEditing ? (
                <Textarea
                  value={editedDescription}
                  onChange={(e) => setEditedDescription(e.target.value)}
                  rows={3}
                  className="w-full"
                  aria-label="Activity description"
                />
              ) : (
                <>
                  <p
                    className={`text-sm text-foreground whitespace-pre-wrap ${
                      isLongDescription && !expanded ? 'line-clamp-2' : ''
                    }`}
                  >
                    {activity.description}
                  </p>
                  {isLongDescription && (
                    <button
                      type="button"
                      onClick={() => setExpanded((v) => !v)}
                      aria-expanded={expanded}
                      aria-label={
                        expanded ? 'Show less of this activity' : 'Show more of this activity'
                      }
                      className="mt-0.5 text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                    >
                      {expanded ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {/* Metadata Row */}
          <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
            {/* Date/Time */}
            <div className="flex items-center gap-1" title={dateTitle}>
              <Clock className="h-3.5 w-3.5" aria-hidden="true" />
              <span>{dateLabel}</span>
            </div>

            {/* Assigned contact (who the activity relates to) */}
            {contactName && (
              <div className="flex items-center gap-1" title={`Contact: ${contactName}`}>
                <UserRound className="h-3.5 w-3.5" aria-hidden="true" />
                {whoHref ? (
                  <Link
                    to={whoHref}
                    className="text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                    aria-label={`View ${whoType === 'Lead' ? 'lead' : 'contact'} ${contactName}`}
                  >
                    {contactName}
                  </Link>
                ) : (
                  <span>{contactName}</span>
                )}
              </div>
            )}

            {/* Contact email */}
            {whoEmail && (
              <div className="flex items-center gap-1" title={`Email: ${whoEmail}`}>
                <Mail className="h-3.5 w-3.5" aria-hidden="true" />
                <a
                  href={`mailto:${whoEmail}`}
                  className="text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                  aria-label={`Email ${contactName || whoEmail}`}
                >
                  {whoEmail}
                </a>
              </div>
            )}

            {/* Owner */}
            {activity.owner_name && (
              <div className="flex items-center gap-1" title={`Owner: ${activity.owner_name}`}>
                <User className="h-3.5 w-3.5" aria-hidden="true" />
                <span>{activity.owner_name}</span>
              </div>
            )}

            {/* Location (for events) */}
            {activity.metadata.location && (
              <div className="flex items-center gap-1">
                <MapPin className="h-4 w-4" aria-hidden="true" />
                <span>{activity.metadata.location}</span>
              </div>
            )}

            {/* Duration (for events) */}
            {activity.metadata.duration_in_minutes && (
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" aria-hidden="true" />
                <span>{activity.metadata.duration_in_minutes} min</span>
              </div>
            )}

            {/* Call details */}
            {activity.activity_type === 'call' && (
              <div className="flex items-center gap-1">
                <Phone className="h-4 w-4" aria-hidden="true" />
                <span>
                  {activity.metadata.incoming ? 'Inbound' : 'Outbound'}
                  {activity.metadata.duration_in_minutes != null &&
                    activity.metadata.duration_in_minutes > 0 && (
                      <>
                        {' '}
                        &middot;{' '}
                        {Math.floor(activity.metadata.duration_in_minutes / 60) > 0
                          ? `${Math.floor(activity.metadata.duration_in_minutes / 60)}m ${activity.metadata.duration_in_minutes % 60}s`
                          : `${activity.metadata.duration_in_minutes}s`}
                      </>
                    )}
                </span>
              </div>
            )}

            {/* Email direction */}
            {activity.activity_type === 'email' && (
              <div className="flex items-center gap-1">
                <Mail className="h-4 w-4" aria-hidden="true" />
                <span>
                  {activity.metadata.incoming ? 'From' : 'To'}:{' '}
                  {activity.metadata.incoming
                    ? activity.metadata.from_address
                    : activity.metadata.to_address}
                </span>
              </div>
            )}

            {/* Priority (for tasks) */}
            {activity.metadata.priority && (
              <Badge
                variant={
                  activity.metadata.priority === 'High'
                    ? 'destructive'
                    : activity.metadata.priority === 'Low'
                      ? 'secondary'
                      : 'default'
                }
                className="text-xs"
              >
                {activity.metadata.priority}
              </Badge>
            )}
          </div>
        </div>
      </div>
    </Card>
  )
}
